import React, { useState, useEffect } from "react";
import { Play, ExternalLink, Youtube, Image as ImageIcon, Film, Filter } from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

// CNESS YouTube Channel ID
const YOUTUBE_CHANNEL_ID = "UCKHXMUepzPdo3gID_E-Ectw";
const YOUTUBE_CHANNEL_URL = "https://www.youtube.com/@CNESSinc";

// Featured YouTube Videos (curated selection)
const FEATURED_VIDEOS = [
  {
    id: "m1OABq0EX58",
    title: "CNESS Social Media SuperApp",
    description: "Discover the consciousness-first social community and wellness app.",
    thumbnail: "https://img.youtube.com/vi/m1OABq0EX58/maxresdefault.jpg"
  },
  {
    id: "0Z6OsFUdF9I",
    title: "Swami Vivekananda — A Voice Rooted in Consciousness",
    description: "Exploring the wisdom of conscious living through timeless teachings.",
    thumbnail: "https://img.youtube.com/vi/0Z6OsFUdF9I/maxresdefault.jpg"
  },
  {
    id: "VLjNg-kA2N0",
    title: "The Value of CNESS Certification",
    description: "Nandhiji explains the benefits for conscious, sustainable growth.",
    thumbnail: "https://img.youtube.com/vi/VLjNg-kA2N0/maxresdefault.jpg"
  },
  {
    id: "eupFewcsRyw",
    title: "The Depth of CNESS Certification",
    description: "Understanding purpose and depth for aligned leadership.",
    thumbnail: "https://img.youtube.com/vi/eupFewcsRyw/maxresdefault.jpg"
  },
  {
    id: "kkQ9SQ2taps",
    title: "Lead with Purpose, Verified by CNESS",
    description: "Introduction to certification for purpose-aligned organizations.",
    thumbnail: "https://img.youtube.com/vi/kkQ9SQ2taps/maxresdefault.jpg"
  },
  {
    id: "Sm4VJaoBm0U",
    title: "Building a Global Conscious Community",
    description: "How CNESS connects people worldwide via shared awareness.",
    thumbnail: "https://img.youtube.com/vi/Sm4VJaoBm0U/maxresdefault.jpg"
  },
  {
    id: "2B7aMYdCo1I",
    title: "The CNESS Ecosystem",
    description: "Overview of platforms and initiatives integrating consciousness.",
    thumbnail: "https://img.youtube.com/vi/2B7aMYdCo1I/maxresdefault.jpg"
  }
];

// Gallery Images (from cness.co style)
const GALLERY_IMAGES = [
  {
    id: 1,
    title: "CNESS Summit 2026",
    category: "Events",
    image: "https://framerusercontent.com/images/am2Wi7OzKsLBbHdixIxKuCeNdYQ.jpg"
  },
  {
    id: 2,
    title: "Product Launch",
    category: "Events",
    image: "https://framerusercontent.com/images/Bi2GC7jVLYjBpD0zBJKbED8Oq8.jpg"
  },
  {
    id: 3,
    title: "Team Collaboration",
    category: "Culture",
    image: "https://framerusercontent.com/images/NLBQljhnJjXiauPZDrzVyGRch6Q.jpg"
  },
  {
    id: 4,
    title: "Digital Workspace",
    category: "Technology",
    image: "https://framerusercontent.com/images/x60fVBblDXWFohIJwPrm0cDqPwM.jpg"
  },
  {
    id: 5,
    title: "Mobile First",
    category: "Technology",
    image: "https://framerusercontent.com/images/TC3kVfOUdD95vqRqP8PTn4EWg3A.jpg"
  },
  {
    id: 6,
    title: "Remote Connectivity",
    category: "Culture",
    image: "https://framerusercontent.com/images/AVRTxXwJc4f6t6QMsKyAWRhIxo.jpg"
  }
];

const GalleryPage = () => {
  const [activeTab, setActiveTab] = useState('videos');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [imageFilter, setImageFilter] = useState('All');

  const categories = ['All', ...new Set(GALLERY_IMAGES.map(img => img.category))];
  const filteredImages = imageFilter === 'All' 
    ? GALLERY_IMAGES 
    : GALLERY_IMAGES.filter(img => img.category === imageFilter);

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-[#F8FAFC] to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
              <Film className="w-4 h-4 text-[#FDB813]" />
              <span className="text-sm font-medium text-[#1E3A5F]">CNESS in Action</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1E3A5F] mb-6">
              Gallery & Media
            </h1>
            <p className="text-lg text-gray-600">
              A visual journal of CNESS initiatives, events and milestones across our conscious enterprise journey.
            </p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b bg-white sticky top-16 lg:top-20 z-30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('videos')}
              className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'videos'
                  ? 'border-[#1E3A5F] text-[#1E3A5F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Youtube className="w-4 h-4 inline mr-2" />
              Videos
            </button>
            <button
              onClick={() => setActiveTab('photos')}
              className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'photos'
                  ? 'border-[#1E3A5F] text-[#1E3A5F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <ImageIcon className="w-4 h-4 inline mr-2" />
              Photos
            </button>
          </div>
        </div>
      </section>

      {/* Videos Section */}
      {activeTab === 'videos' && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {/* Featured Video Player */}
            <div className="mb-16">
              <h2 className="text-2xl font-bold text-[#1E3A5F] mb-6">Conscious Video Vault</h2>
              <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
                <iframe
                  src={`https://www.youtube.com/embed/${selectedVideo || FEATURED_VIDEOS[0].id}?autoplay=0&rel=0`}
                  title="CNESS Video"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>

            {/* Video Grid */}
            <div className="mb-8 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-[#1E3A5F]">Latest Videos</h3>
              <a 
                href={YOUTUBE_CHANNEL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-sm text-[#1E3A5F] hover:text-[#FDB813] transition-colors"
              >
                View all on YouTube
                <ExternalLink className="w-4 h-4 ml-1" />
              </a>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {FEATURED_VIDEOS.map((video) => (
                <div 
                  key={video.id}
                  onClick={() => setSelectedVideo(video.id)}
                  className={`group cursor-pointer rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all ${
                    selectedVideo === video.id ? 'ring-2 ring-[#FDB813]' : ''
                  }`}
                >
                  <div className="relative aspect-video">
                    <img 
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                      }}
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
                        <Play className="w-6 h-6 text-[#1E3A5F] ml-1" />
                      </div>
                    </div>
                    {selectedVideo === video.id && (
                      <div className="absolute top-2 right-2 bg-[#FDB813] text-[#1E3A5F] text-xs font-semibold px-2 py-1 rounded">
                        Now Playing
                      </div>
                    )}
                  </div>
                  <div className="p-4 bg-white">
                    <h4 className="font-semibold text-[#1E3A5F] text-sm line-clamp-2 mb-1">
                      {video.title}
                    </h4>
                    <p className="text-xs text-gray-500 line-clamp-2">
                      {video.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* YouTube Channel CTA */}
            <div className="mt-12 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-8 text-white text-center">
              <Youtube className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Subscribe to CNESS on YouTube</h3>
              <p className="text-white/80 mb-6">Stay updated with our latest videos, insights, and conscious business content.</p>
              <a 
                href={YOUTUBE_CHANNEL_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-3 rounded-xl font-semibold">
                  Subscribe Now
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Photos Section */}
      {activeTab === 'photos' && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {/* Filter */}
            <div className="flex items-center gap-4 mb-8">
              <Filter className="w-5 h-5 text-gray-400" />
              <div className="flex gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setImageFilter(cat)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      imageFilter === cat
                        ? 'bg-[#1E3A5F] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Photo Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredImages.map((image) => (
                <div 
                  key={image.id}
                  className="group relative aspect-[4/3] rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all"
                >
                  <img 
                    src={image.image}
                    alt={image.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                    <span className="text-xs font-medium text-[#FDB813] mb-1 block">{image.category}</span>
                    <h4 className="text-white font-semibold">{image.title}</h4>
                  </div>
                </div>
              ))}
            </div>

            {filteredImages.length === 0 && (
              <div className="text-center py-12">
                <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No photos found in this category.</p>
              </div>
            )}
          </div>
        </section>
      )}
    </PublicLayout>
  );
};

export default GalleryPage;
